import { AxiosRequestConfig } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { APIPaginatedRequest } from '../../models/APIPaginatedRequest';
import { APIPaginatedResponse } from '../../models/APIPaginatedResponse';
import Service from '../../services/Service';

export interface InfiniteScrollRequest {
  page: number;
  search: string;
  orderBy: string;
  pageSize: number;
  referenceDate: Date | null;
  orderDirection: 'asc' | 'desc';
}

export interface InfiniteScrollResponse<T> {
  items: T[];
  page: number;
  totalPages: number;
  totalCount: number;
  newItems: number | T[];
  referenceDate: Date | null;
}

export type InifiniteScrollProps<T extends object, R extends object> = AxiosRequestConfig & {
  load: (request: APIPaginatedRequest<R>, signal?: AbortSignal) => Promise<APIPaginatedResponse<T>>;
  search?: R | Record<string, never>;
  orderBy?: keyof T | '';
  pageSize?: number;
  firstPage?: number;
  orderDirection?: 'asc' | 'desc';
};

const ROOT_MARGIN = '100px';

export default function useInfiniteScroll<T extends object, R extends object>({
  load,
  search,
  firstPage = 1,
  pageSize = 10
}: InifiniteScrollProps<T, R>) {
  // States
  const [error, setError] = useState(false);
  const [hasNext, setHasNext] = useState(true);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<Array<T>>([]);
  const [paginaSelecionada, setPaginaSelecionada] = useState(firstPage);

  // Refs
  const observer = useRef<IntersectionObserver>();

  const lastElementRef = (node: HTMLElement | null) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      (entries) => {
        const [lastElement] = entries;
        if (lastElement.isIntersecting) setPaginaSelecionada((prev) => prev + 1);
      },
      { rootMargin: ROOT_MARGIN }
    );
    if (node) observer.current.observe(node);
  };

  const buscar = useCallback(
    async (signal?: AbortSignal) => {
      setError(false);
      setLoading(true);

      const request = {
        page: paginaSelecionada,
        pageSize: pageSize,
        search: search ? search : {}
      };

      load
        .call(Service, request, signal)
        .then((response) => {
          if (signal?.aborted) return;
          if (response) {
            setHasNext(response.page < response.totalCount / response.pageSize);
            setItems((prev) => prev.concat(response.data));
            setLoading(false);
          }
        })
        .catch(() => {
          if (signal?.aborted) return;
          setError(true);
          setLoading(false);
        });
    },
    [paginaSelecionada, pageSize, search, load]
  );

  const refresh = useCallback(() => {
    setItems([]);
    if (firstPage === paginaSelecionada) buscar();
    else setPaginaSelecionada(firstPage);
  }, [buscar, firstPage, paginaSelecionada]);

  useEffect(() => {
    setItems([]);
    setPaginaSelecionada(firstPage);
  }, [firstPage, search]);

  useEffect(() => {
    const controller = new AbortController();
    buscar(controller.signal);

    return () => {
      controller.abort();
    };
  }, [buscar]);

  return {
    error,
    items,
    hasNext,
    loading,
    refresh,
    lastElementRef
  };
}
